<template>
	<form id="form" method="post" action="" @submit.prevent="onSubmit">
		<span id="upload-progressbar" />
		<span id="nick">{{ network.nick }}</span>
		<label for="input" class="sr-only">Message input</label>
		<textarea
			id="input"
			ref="input"
			dir="auto"
			class="mousetrap"
			enterkeyhint="send"
			autocomplete="off"
			:value="channel.pendingMessage"
			:placeholder="getInputPlaceholder(channel)"
			@input="setPendingMessage"
			@keypress.enter.exact.prevent="onSubmit"
			@blur="onBlur"
		/>
		<span ref="emojiPickerWrapper" id="emoji-tooltip" class="tooltipped tooltipped-w tooltipped-no-touch" aria-label="Emoji" @click="toggleEmojiPicker">
			<button id="emoji-picker-btn" type="button" aria-label="Emoji" />
			<div v-if="showEmojiPicker" id="emoji-picker-panel">
				<button
					v-for="e in quickEmojis"
					:key="e"
					type="button"
					class="emoji-picker-item"
					@click.stop="insertEmoji(e)"
				>{{ e }}</button>
			</div>
		</span>
		<span
			v-if="store.state.serverConfiguration?.fileUpload"
			id="upload-tooltip"
			class="tooltipped tooltipped-w tooltipped-no-touch"
			aria-label="Upload file"
		>
			<input
				id="upload-input"
				ref="uploadInput"
				type="file"
				aria-labelledby="upload"
				multiple
				@change="onUploadInputChange"
			/>
			<button
				id="upload"
				type="button"
				aria-label="Upload file"
				:disabled="!store.state.isConnected"
				@click="openFileUpload"
			/>
		</span>
		<span
			id="submit-tooltip"
			class="tooltipped tooltipped-w tooltipped-no-touch"
			data-tooltip="Send message"
		>
			<button
				id="submit"
				type="submit"
				aria-label="Send message"
				:disabled="!store.state.isConnected"
			/>
		</span>
	</form>
</template>

<script lang="ts">
import Mousetrap from "mousetrap";
import {wrapCursor} from "undate";
import autocompletion from "../js/autocompletion";
import {commands} from "../js/commands/index";
import socket from "../js/socket";
import upload from "../js/upload";
import eventbus from "../js/eventbus";
import {watch, defineComponent, nextTick, onMounted, PropType, ref, onUnmounted} from "vue";
import type {ClientNetwork, ClientChan} from "../js/types";
import {useStore} from "../js/store";
import {ChanType} from "../../shared/types/chan";

const formattingHotkeys = {
	"mod+k": "\x03",
	"mod+b": "\x02",
	"mod+u": "\x1F",
	"mod+i": "\x1D",
	"mod+o": "\x0F",
	"mod+s": "\x1e",
	"mod+m": "\x11",
};

// Autocomplete bracket and quote characters like in a modern IDE
// For example, select `text`, press `[` key, and it becomes `[text]`
const bracketWraps = {
	'"': '"',
	"'": "'",
	"(": ")",
	"<": ">",
	"[": "]",
	"{": "}",
	"*": "*",
	"`": "`",
	"~": "~",
	_: "_",
};

// ЛАФЧЕ.ORG: ASCII emoticon -> Unicode emoji auto-replace, приложено
// само на нормални съобщения (не slash команди), точно преди изпращане.
// Историята (inputHistory) пази оригиналния текст, замяната важи само
// за изпратеното съобщение.
const EMOTICON_MAP: Record<string, string> = {
	"(hug)": "🤗",
	"(bear)": "🤗",
	"</3": "💔",
	"<3": "❤️",
	":'(": "😢",
	">:-(": "😠",
	">:(": "😠",
	":-)": "🙂",
	":)": "🙂",
	":]": "🙂",
	":-(": "🙁",
	":(": "🙁",
	":[": "🙁",
	// смях със сълзи, ЗАВЪРТЯНО настрани
	":-D": "🤣",
	":D": "🤣",
	":Д": "🤣",
	";-D": "🤣",
	";D": "🤣",
	";Д": "🤣",
	// смях, присвити очи (различно семейство от горното/долното)
	XD: "😆",
	xD: "😆",
	// смях със сълзи, ИЗПРАВЕНО (нормална позиция)
	":d": "😂",
	":д": "😂",
	";d": "😂",
	";д": "😂",
	";-)": "😉",
	";)": "😉",
	":-P": "😛",
	":-p": "😛",
	":P": "😛",
	":p": "😛",
	":п": "😛",
	":П": "😛",
	";-P": "😜",
	";-p": "😜",
	";P": "😜",
	";p": "😜",
	";п": "😜",
	";П": "😜",
	":-*": "😘",
	":*": "😘",
	";*": "😘",
	"{}": "💋",
	"()": "💋",
	"[]": "💋",
	":-O": "😮",
	":-o": "😮",
	":O": "😮",
	":o": "😮",
	":-|": "😐",
	":|": "😐",
	"8)": "😎",
	"B)": "😎",
	":-/": "😕",
	":-\\": "😕",
	":/": "😕",
	":\\": "😕",
};

const EMOTICON_KEYS = Object.keys(EMOTICON_MAP).sort((a, b) => b.length - a.length);
const EMOTICON_REGEX = new RegExp(
	EMOTICON_KEYS.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|"),
	"g"
);

// ЛАФЧЕ.ORG: URL-ите се пазят непокътнати - "://" вътре в линк не бива
// да се превръща в emoji (счупва линка). Затова текстът се разделя на
// URL/не-URL сегменти и emoticon replace важи само извън линковете.
const URL_REGEX = /https?:\/\/\S+/gi;

function applyEmoticonReplacements(text: string): string {
	const trimmed = text.trim();

	// самотно "p"/"P" като ЦЯЛОТО съобщение = плезене emoji - не пипаме
	// p/P вътре в нормален текст (твърде чест символ, за да е глобален)
	if (trimmed === "p" || trimmed === "P" || trimmed === "р" || trimmed === "Р") {
		return "😛";
	}

	const parts = text.split(URL_REGEX);
	const urls = text.match(URL_REGEX) || [];

	let result = "";

	for (let i = 0; i < parts.length; i++) {
		result += parts[i].replace(EMOTICON_REGEX, (match) => EMOTICON_MAP[match] ?? match);

		if (i < urls.length) {
			result += urls[i];
		}
	}

	return result;
}

// ЛАФЧЕ.ORG: 48 стандартни emoji за бърз picker панел (WhatsApp/Viber/
// Messenger стил), достъпни от бутона до upload иконата.
const QUICK_EMOJIS = [
	"😀", "😁", "😂", "🤣", "😊", "😇", "🙂", "😉", "😍", "🥰",
	"😘", "😋", "😜", "🤪", "😎", "🥳", "😏", "😒", "😔", "😢",
	"😭", "😡", "🤬", "😱", "😨", "😴", "🤤", "🤔", "🤗", "🙄",
	"😳", "🥵", "🥶", "👍", "👎", "👌", "✌️", "🤞", "👏", "🙏",
	"💪", "👋", "❤️", "💔", "🔥", "✨", "🎉", "💯",
];

export default defineComponent({
	name: "ChatInput",
	props: {
		network: {type: Object as PropType<ClientNetwork>, required: true},
		channel: {type: Object as PropType<ClientChan>, required: true},
	},
	setup(props) {
		const store = useStore();
		const input = ref<HTMLTextAreaElement>();
		const uploadInput = ref<HTMLInputElement>();
		const autocompletionRef = ref<ReturnType<typeof autocompletion>>();

		const setInputSize = () => {
			void nextTick(() => {
				if (!input.value) {
					return;
				}

				const style = window.getComputedStyle(input.value);
				const lineHeight = parseFloat(style.lineHeight) || 1;

				// Start by resetting height before computing as scrollHeight does not
				// decrease when deleting characters
				input.value.style.height = "";

				// Use scrollHeight to calculate how many lines there are in input, and ceil the value
				// because some browsers tend to incorrently round the values when using high density
				// displays or using page zoom feature
				input.value.style.height = `${
					Math.ceil(input.value.scrollHeight / lineHeight) * lineHeight
				}px`;
			});
		};

		const setPendingMessage = (e: Event) => {
			props.channel.pendingMessage = (e.target as HTMLInputElement).value;
			props.channel.inputHistoryPosition = 0;
			setInputSize();
		};

		const getInputPlaceholder = (channel: ClientChan) => {
			if (channel.type === ChanType.CHANNEL || channel.type === ChanType.QUERY) {
				return `Write to ${channel.name}`;
			}

			return "";
		};

		const onSubmit = () => {
			if (!input.value) {
				return;
			}

			// Triggering click event opens the virtual keyboard on mobile
			// This can only be called from another interactive event (e.g. button click)
			input.value.click();
			input.value.focus();

			if (!store.state.isConnected) {
				return false;
			}

			const target = props.channel.id;
			let text = props.channel.pendingMessage;

			if (text.length === 0) {
				return false;
			}

			if (autocompletionRef.value) {
				autocompletionRef.value.hide();
			}

			props.channel.inputHistoryPosition = 0;
			props.channel.pendingMessage = "";
			input.value.value = "";
			setInputSize();

			// Store new message in history if last message isn't already equal
			if (props.channel.inputHistory[1] !== text) {
				props.channel.inputHistory.splice(1, 0, text);
			}

			// Limit input history to a 100 entries
			if (props.channel.inputHistory.length > 100) {
				props.channel.inputHistory.pop();
			}

			if (text[0] === "/") {
				const args = text.substring(1).split(" ");
				const cmd = args.shift()?.toLowerCase();

				if (!cmd) {
					return false;
				}

				if (Object.prototype.hasOwnProperty.call(commands, cmd) && commands[cmd](args)) {
					return false;
				}
			} else {
				text = applyEmoticonReplacements(text);
			}

			socket.emit("input", {target, text});
		};

		const onUploadInputChange = () => {
			if (!uploadInput.value || !uploadInput.value.files) {
				return;
			}

			const files = Array.from(uploadInput.value.files);
			upload.triggerUpload(files);
			uploadInput.value.value = ""; // Reset <input> element so you can upload the same file
		};

		const openFileUpload = () => {
			const dailyLimit = store.state.serverConfiguration?.fileUploadDailyLimit || 10;

			eventbus.emit(
				"confirm-dialog",
				{
					title: "Изпращане на снимка",
					text: `1. Само JPEG формат снимки.\n2. ${dailyLimit} снимки лимит на ден.\n3. Автоматично изтриване след 1 час.`,
					button: "Продължи",
				},
				(confirmed: boolean) => {
					if (confirmed) {
						uploadInput.value?.click();
					}
				}
			);
		};

		const blurInput = () => {
			input.value?.blur();
		};

		const onBlur = () => {
			if (autocompletionRef.value) {
				autocompletionRef.value.hide();
			}
		};

		watch(
			() => props.channel.id,
			() => {
				if (autocompletionRef.value) {
					autocompletionRef.value.hide();
				}
			}
		);

		watch(
			() => props.channel.pendingMessage,
			() => {
				setInputSize();
			}
		);

		// ЛАФЧЕ.ORG: бърз nick-mention - клик на ник в съобщенията вкарва
		// ника на позицията на курсора (замества евентуален избран текст),
		// а не винаги в началото - и фокусира полето за писане. Двоеточие
		// (класическата IRC "обръщение към") само ако вмъкваме в самото
		// начало на текста - иначе е просто споменаване по средата на
		// изречение, и добавяме само интервал.
		const insertNick = (nick: string) => {
			const el = input.value;
			const current = props.channel.pendingMessage;

			if (!el) {
				props.channel.pendingMessage = current.length === 0 ? nick + ": " : current + nick + " ";
				setInputSize();
				return;
			}

			const start = el.selectionStart ?? current.length;
			const end = el.selectionEnd ?? start;
			const insertText = start === 0 ? nick + ": " : nick + " ";

			props.channel.pendingMessage = current.slice(0, start) + insertText + current.slice(end);
			setInputSize();

			void nextTick(() => {
				el.focus();
				const newPos = start + insertText.length;
				el.setSelectionRange(newPos, newPos);
			});
		};

		// ЛАФЧЕ.ORG: emoji quick-picker панел (48 стандартни emoji) - клик
		// на бутона отваря/затваря панела, клик на emoji го вкарва на
		// позицията на курсора (по същия принцип като insertNick по-горе)
		// и затваря панела.
		const showEmojiPicker = ref(false);
		const emojiPickerWrapper = ref<HTMLElement>();
		const quickEmojis = QUICK_EMOJIS;

		const toggleEmojiPicker = () => {
			showEmojiPicker.value = !showEmojiPicker.value;
		};

		// ЛАФЧЕ.ORG: затваря панела при клик/тап ВЪН от него - стандартно
		// UX поведение. Кликът на самия toggle бутон е ВЪТРЕ в wrapper-а,
		// затова не се затваря веднага след отваряне; изборът на emoji
		// вътре в панела ползва @click.stop, затова не стига до document
		// изобщо (insertEmoji() затваря панела сам).
		const onDocumentClick = (e: MouseEvent) => {
			if (
				showEmojiPicker.value &&
				emojiPickerWrapper.value &&
				!emojiPickerWrapper.value.contains(e.target as Node)
			) {
				showEmojiPicker.value = false;
			}
		};

		const insertEmoji = (emoji: string) => {
			const el = input.value;
			const current = props.channel.pendingMessage;

			if (!el) {
				props.channel.pendingMessage = current + emoji;
				setInputSize();
				showEmojiPicker.value = false;
				return;
			}

			const start = el.selectionStart ?? current.length;
			const end = el.selectionEnd ?? start;

			props.channel.pendingMessage = current.slice(0, start) + emoji + current.slice(end);
			setInputSize();
			showEmojiPicker.value = false;

			void nextTick(() => {
				el.focus();
				const newPos = start + emoji.length;
				el.setSelectionRange(newPos, newPos);
			});
		};

		onMounted(() => {
			eventbus.on("escapekey", blurInput);
			eventbus.on("chat-input:insert-nick", insertNick);
			document.addEventListener("click", onDocumentClick);

			if (store.state.settings.autocomplete) {
				if (!input.value) {
					throw new Error("ChatInput autocomplete: input element is not available");
				}

				autocompletionRef.value = autocompletion(input.value);
			}

			const inputTrap = Mousetrap(input.value);

			inputTrap.bind(Object.keys(formattingHotkeys), function (e, key) {
				const modifier = formattingHotkeys[key];

				if (!e.target) {
					return;
				}

				wrapCursor(
					e.target as HTMLTextAreaElement,
					modifier,
					(e.target as HTMLTextAreaElement).selectionStart ===
						(e.target as HTMLTextAreaElement).selectionEnd
						? ""
						: modifier
				);

				return false;
			});

			inputTrap.bind(Object.keys(bracketWraps), function (e, key) {
				if (
					(e.target as HTMLTextAreaElement)?.selectionStart !==
					(e.target as HTMLTextAreaElement).selectionEnd
				) {
					wrapCursor(e.target as HTMLTextAreaElement, key, bracketWraps[key]);

					return false;
				}
			});

			inputTrap.bind(["up", "down"], (e, key) => {
				if (
					store.state.isAutoCompleting ||
					(e.target as HTMLTextAreaElement).selectionStart !==
						(e.target as HTMLTextAreaElement).selectionEnd ||
					!input.value
				) {
					return;
				}

				const onRow = (
					input.value.value.slice(undefined, input.value.selectionStart).match(/\n/g) ||
					[]
				).length;
				const totalRows = (input.value.value.match(/\n/g) || []).length;

				const {channel} = props;

				if (channel.inputHistoryPosition === 0) {
					channel.inputHistory[channel.inputHistoryPosition] = channel.pendingMessage;
				}

				if (key === "up" && onRow === 0) {
					if (channel.inputHistoryPosition < channel.inputHistory.length - 1) {
						channel.inputHistoryPosition++;
					} else {
						return;
					}
				} else if (
					key === "down" &&
					channel.inputHistoryPosition > 0 &&
					onRow === totalRows
				) {
					channel.inputHistoryPosition--;
				} else {
					return;
				}

				channel.pendingMessage = channel.inputHistory[channel.inputHistoryPosition];
				input.value.value = channel.pendingMessage;
				setInputSize();

				return false;
			});

			if (store.state.serverConfiguration?.fileUpload) {
				upload.mounted();
			}
		});

		onUnmounted(() => {
			eventbus.off("escapekey", blurInput);
			eventbus.off("chat-input:insert-nick", insertNick);
			document.removeEventListener("click", onDocumentClick);

			if (autocompletionRef.value) {
				autocompletionRef.value.destroy();
				autocompletionRef.value = undefined;
			}

			upload.unmounted();
			upload.abort();
		});

		return {
			store,
			input,
			uploadInput,
			onUploadInputChange,
			openFileUpload,
			blurInput,
			onBlur,
			setInputSize,
			upload,
			getInputPlaceholder,
			onSubmit,
			setPendingMessage,
			showEmojiPicker,
			emojiPickerWrapper,
			toggleEmojiPicker,
			quickEmojis,
			insertEmoji,
		};
	},
});
</script>
